import React from "react";
import { Wand2, ChevronUp, ChevronDown, Target, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const AIToolsPanel = ({
  showTools,
  setShowTools,
  analyzingBeat,
  handleAnalyzeBeat,
  canAnalyzeBeat,
  checkingThumbnail,
  handleThumbnailCheck,
  beatAnalysis,
  thumbnailCheckResult,
  onUpgrade,
}) => {
  const actionButtonStyle = (accent) => ({
    color: accent,
    borderColor: `color-mix(in srgb, ${accent} 34%, var(--border-color))`,
    backgroundColor: "transparent",
  });

  return (
    <Card className="border-l-4 border-l-emerald-500 shadow-sm">
      <CardHeader className="pb-3 flex flex-row items-center justify-between">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <Wand2 className="h-4 w-4" /> AI Tools
        </CardTitle>
        <Button size="sm" variant="ghost" onClick={() => setShowTools(!showTools)}>
          {showTools ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </Button>
      </CardHeader>
      {showTools && (
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Button
              variant="outline"
              onClick={canAnalyzeBeat ? handleAnalyzeBeat : onUpgrade}
              disabled={analyzingBeat}
              className="transition-colors hover:bg-secondary/70"
              style={actionButtonStyle("var(--accent-primary)")}
            >
              {analyzingBeat ? "..." : <><Target className="mr-2 h-4 w-4" />{canAnalyzeBeat ? "Analyze Beat" : "Analyze Beat (Pro)"}</>}
            </Button>
            <Button
              variant="outline"
              onClick={handleThumbnailCheck}
              disabled={checkingThumbnail}
              className="transition-colors hover:bg-secondary/70"
              style={actionButtonStyle("var(--accent-secondary)")}
            >
              {checkingThumbnail ? "..." : <><Sparkles className="mr-2 h-4 w-4" /> Check Thumb</>}
            </Button>
          </div>

          {!canAnalyzeBeat ? (
            <div className="rounded-md border border-border bg-secondary/20 p-3 text-xs text-muted-foreground">
              Beat analysis is on paid plans. Free users can still use thumbnail check here.
            </div>
          ) : null}

          {beatAnalysis && (
            <div className="p-3 bg-secondary/50 rounded-md text-sm space-y-2">
              <div className="flex justify-between font-bold">
                <span>Score: {beatAnalysis.overall_score}/100</span>
                <span>{beatAnalysis.predicted_performance}</span>
              </div>
              <div className="grid grid-cols-1 gap-2 text-xs sm:grid-cols-2">
                {beatAnalysis.channel_stage ? (
                  <div className="rounded border border-border bg-background/40 p-2">
                    <div className="font-semibold">Channel Stage</div>
                    <div className="text-muted-foreground">{beatAnalysis.channel_stage}</div>
                  </div>
                ) : null}
                {beatAnalysis.competition_level ? (
                  <div className="rounded border border-border bg-background/40 p-2">
                    <div className="font-semibold">Competition</div>
                    <div className="text-muted-foreground">{beatAnalysis.competition_level}</div>
                  </div>
                ) : null}
                {beatAnalysis.packaging_priority ? (
                  <div className="rounded border border-border bg-background/40 p-2">
                    <div className="font-semibold">Main Fix</div>
                    <div className="text-muted-foreground">{beatAnalysis.packaging_priority}</div>
                  </div>
                ) : null}
              </div>
              {beatAnalysis.title_diagnosis ? (
                <div className="text-xs text-muted-foreground">
                  <span className="font-semibold text-foreground">Title diagnosis: </span>
                  {beatAnalysis.title_diagnosis}
                </div>
              ) : null}
              {beatAnalysis.channel_fit ? (
                <div className="text-xs text-muted-foreground">
                  <span className="font-semibold text-foreground">Channel fit: </span>
                  {beatAnalysis.channel_fit}
                </div>
              ) : null}
              {beatAnalysis.small_channel_strategy ? (
                <div className="text-xs text-muted-foreground">
                  <span className="font-semibold text-foreground">Strategy: </span>
                  {beatAnalysis.small_channel_strategy}
                </div>
              ) : null}
              {beatAnalysis.better_title_angles?.length > 0 ? (
                <div className="text-xs">
                  <div className="font-semibold">Better Title Angles</div>
                  <ul className="list-disc pl-4 text-muted-foreground space-y-1">
                    {beatAnalysis.better_title_angles.slice(0, 3).map((angle, idx) => (
                      <li key={`angle-${idx}`}>{angle}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {beatAnalysis.tag_strategy ? (
                <div className="text-xs text-muted-foreground">
                  <span className="font-semibold text-foreground">Tag strategy: </span>
                  {beatAnalysis.tag_strategy}
                </div>
              ) : null}
              {beatAnalysis.suggestions?.length > 0 ? (
                <div className="text-xs">
                  <div className="font-semibold">Best Next Moves</div>
                  <ul className="list-disc pl-4 text-muted-foreground space-y-1">
                    {beatAnalysis.suggestions.slice(0, 3).map((suggestion, idx) => (
                      <li key={`beat-suggestion-${idx}`}>{suggestion}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          )}
          {thumbnailCheckResult && (
            <div className="p-3 bg-secondary/50 rounded-md text-sm space-y-2">
              <div className="flex justify-between font-bold">
                <span>Score: {thumbnailCheckResult.score}/100</span>
                <span>{thumbnailCheckResult.verdict}</span>
              </div>
              <div className="grid grid-cols-1 gap-2 text-xs sm:grid-cols-2">
                {thumbnailCheckResult.ctr_risk ? (
                  <div className="rounded border border-border bg-background/40 p-2">
                    <div className="font-semibold">CTR Risk</div>
                    <div className="text-muted-foreground">{thumbnailCheckResult.ctr_risk}</div>
                  </div>
                ) : null}
                {thumbnailCheckResult.packaging_priority ? (
                  <div className="rounded border border-border bg-background/40 p-2">
                    <div className="font-semibold">Main Fix</div>
                    <div className="text-muted-foreground">{thumbnailCheckResult.packaging_priority}</div>
                  </div>
                ) : null}
              </div>
              {thumbnailCheckResult.hook_alignment ? (
                <div className="text-xs text-muted-foreground">
                  <span className="font-semibold text-foreground">Hook alignment: </span>
                  {thumbnailCheckResult.hook_alignment}
                </div>
              ) : null}
              {thumbnailCheckResult.issues?.length > 0 && (
                <div className="text-xs">
                  <div className="font-semibold">Main Issues</div>
                  <ul className="list-disc pl-4 text-muted-foreground space-y-1">
                    {thumbnailCheckResult.issues.slice(0, 2).map((issue, idx) => (
                      <li key={`issue-${idx}`}>{issue}</li>
                    ))}
                  </ul>
                </div>
              )}
              {thumbnailCheckResult.suggestions?.length > 0 && (
                <div className="text-xs">
                  <div className="font-semibold">Best Next Fixes</div>
                  <ul className="list-disc pl-4 text-muted-foreground space-y-1">
                    {thumbnailCheckResult.suggestions.slice(0, 3).map((tip, idx) => (
                      <li key={`tip-${idx}`}>{tip}</li>
                    ))}
                  </ul>
                </div>
              )}
              {(thumbnailCheckResult.text_overlay_suggestion || thumbnailCheckResult.branding_suggestion) && (
                <div className="text-xs text-muted-foreground space-y-1">
                  {thumbnailCheckResult.text_overlay_suggestion && (
                    <div>
                      <span className="font-semibold">Text Overlay: </span>
                      {thumbnailCheckResult.text_overlay_suggestion}
                    </div>
                  )}
                  {thumbnailCheckResult.branding_suggestion && (
                    <div>
                      <span className="font-semibold">Branding: </span>
                      {thumbnailCheckResult.branding_suggestion}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};

export default AIToolsPanel;
